<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <span v-if="serviceTaskDirty.id">
        Edit Service Task
      </span>
      <span v-else>
        Add New Service Task
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-text-field
        v-model="serviceTaskDirty.title"
        v-validate="'required'"
        data-vv-as="title"
        name="title"
        :error-messages="veeErrors.collect('title')"
        outlined
        label="Title"
        placeholder="Service task title"
      />
      <v-autocomplete
        v-model="serviceTaskDirty.service_task_type_id"
        v-validate="'required'"
        outlined
        label="Select task type"
        data-vv-as="task type"
        name="type"
        :error-messages="veeErrors.collect('type')"
        :items="serviceTaskTypes.data"
        :loading="serviceTaskTypes.loading"
        item-value="id"
        item-text="title"
      />
      <v-subheader>Description</v-subheader>
      <tiny-mce
        id="service_repository_task_description_editor"
        key="service_repository_task_description_editor"
        v-model="serviceTaskDirty.description"
        width="auto"
        simple
      />
      <v-switch
        v-model="serviceTaskDirty.wintering"
        label="Wintering task"
      />
      <v-text-field
        v-model="serviceTaskDirty.order"
        type="number"
        outlined
        label="Order Number"
        placeholder="1"
        style="width: 120px"
      />
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="serviceTaskDirty.id && $can('delete', 'ServiceTasksRepository')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Service Task from Repository?"
      @confirmed="deleteServiceTask"
    />
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ServiceTaskType from '@/models/ServiceTaskType'
import ServiceTaskRepository from '@/models/ServiceTaskRepository'
import ContentDialog from '@/components/helpers/Dialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import TinyMce from '@/components/helpers/TinyMce'

export default {
    name: 'ServiceTaskRepositoryEditDialog',
    components: {
      TinyMce,
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      serviceTask: {
        type: Object,
        default: () => ServiceTaskRepository.emptyModel
      }
    },
    data() {
      return {
        serviceTaskDirty: cloneDeep(ServiceTaskRepository.emptyModel),
        serviceTaskTypes: {
          data: [],
          loading: false
        },
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      }
    },
    watch: {
      serviceTask() {
        this.serviceTaskDirty = cloneDeep(this.serviceTask)
      }
    },
    created() {
      this.fetchServiceTaskTypes()
    },
    methods: {
      async fetchServiceTaskTypes() {
        this.serviceTaskTypes.loading = true

        try {
          this.serviceTaskTypes.data = await ServiceTaskType.api.fetch()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.serviceTaskTypes.loading = false
        }
      },
      async submit() {
        let validate = await this.$validator.validate()

        if (!validate) return

        this.save()
      },
      async save() {
        this.submitting = true

        const data = {
          title: this.serviceTaskDirty.title,
          service_task_type_id: this.serviceTaskDirty.service_task_type_id,
          wintering: this.serviceTaskDirty.wintering,
          description: this.serviceTaskDirty.description,
          order: this.serviceTaskDirty.order
        }

        try {
          let serviceTask = await ServiceTaskRepository.api.save(data, this.serviceTaskDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Service task saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.serviceTaskDirty.id ? 'serviceTaskRepository:updated' : 'serviceTaskRepository:created', serviceTask)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteServiceTask() {
        this.deleting = true

        try {
          await ServiceTaskRepository.api.destroy(this.serviceTaskDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Service task deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('serviceTaskRepository:deleted', this.serviceTaskDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
